/**
 * MyMouvSection — Events & concerts carousel on the Studio home tab.
 * Sits directly below <EsimSection />. Links each card to a full detail page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Ticket, Calendar, MapPin, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

const currencySymbol = (c) => ({ EUR: '€', USD: '$', GBP: '£', CAD: 'CA$', JPY: '¥', CNY: '¥', INR: '₹', BRL: 'R$', RUB: '₽', KRW: '₩', ARS: '$', XOF: 'FCFA', XAF: 'FCFA' }[c] || c);

const MyMouvSection = () => {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/mymouv/events');
      setEvents(r.data?.events || []);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('mymouv:changed', handler);
    return () => window.removeEventListener('mymouv:changed', handler);
  }, [load]);

  const minPrice = (ev) => {
    const paid = (ev.ticket_types || []).filter(t => Number(t.price) > 0);
    if (paid.length === 0) return { free: true };
    const m = paid.reduce((a, b) => (Number(a.price) < Number(b.price) ? a : b));
    return { free: false, price: m.price, currency: m.currency };
  };

  return (
    <section data-testid="mymouv-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-fuchsia-400" />
          <h3 className="text-lg font-bold text-white">MyMouv</h3>
          <span className="text-xs text-white/40 ml-2">{t('mymouv.subtitle', 'Événements & concerts')}</span>
        </div>
        <button
          onClick={() => navigate('/mymouv')}
          className="text-xs text-fuchsia-300 hover:text-fuchsia-200 flex items-center gap-1"
          data-testid="mymouv-see-all"
        >
          {t('mymouvHome.seeAll', 'Voir tout')} <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-white/50">{t('common.loading', 'Chargement…')}</div>
      ) : events.length === 0 ? (
        <div className="text-sm text-white/40">{t('mymouvHome.empty', "Aucun événement pour l'instant.")}</div>
      ) : (
        <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-4">
          {events.slice(0, 12).map((ev) => {
            const price = minPrice(ev);
            return (
              <button
                key={ev.id}
                onClick={() => navigate(`/mymouv/events/${ev.id}`)}
                className="flex-shrink-0 w-52 rounded-2xl bg-gradient-to-br from-fuchsia-900/40 to-purple-900/40 border border-fuchsia-500/30 hover:border-fuchsia-400/60 transition text-left overflow-hidden"
                data-testid={`mymouv-event-card-${ev.id}`}
              >
                <div className="relative h-32 bg-fuchsia-950/60 flex items-center justify-center overflow-hidden">
                  {ev.cover_image_url ? (
                    <img src={getMediaUrl(ev.cover_image_url)} alt={ev.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <Ticket className="w-8 h-8 text-fuchsia-400/60" />
                  )}
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-fuchsia-500 text-white text-xs font-bold">
                    {price.free ? t('mymouvHome.free', 'Gratuit') : `${price.price}${currencySymbol(price.currency)}`}
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-bold text-white line-clamp-1">{ev.title}</h3>
                  <p className="text-xs text-white/60 line-clamp-2 mt-1">{ev.description}</p>
                  <div className="mt-2 text-[11px] text-white/50 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {ev.event_date}
                  </div>
                  <div className="text-[11px] text-white/50 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{ev.location?.name || ev.location?.address}</span>
                  </div>
                  <div className="mt-2 text-[10px] text-fuchsia-300">
                    {ev.tickets_remaining} {t('mymouvHome.seatsRemaining', 'places restantes')}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default MyMouvSection;
